import { Auth_APIS } from "libs/apis/auth.api";
import { CreateBusinessDTO } from "utils/helpers/models/auth/business-register.dto";
import { successToaster, errorToaster } from "utils/helpers/common/alert-service";
import { VerifyOtpDTO } from "utils/helpers/models/auth/otp-verify-signup.dto";
import { useNavigate } from "react-router-dom";
import { siteRoutes } from "utils/helpers/enums/routes";
import useStore from "hooks/useStore";
import { LoginFormDTO } from "utils/helpers/models/auth/login";
import {
  ResendOtpDTO,
  ResetPasswordDTO,
} from "utils/helpers/models/auth/reset-password.dto";
import { LoginByGoogleDTO } from "utils/helpers/models/auth/login-by-google.dto";
import { LoginByAppleDTO } from "utils/helpers/models/auth/login-by-apple.dto";
import { ROLES } from "utils/helpers/enums/roles.enum";
import { SwitchProfileDTO } from "utils/helpers/models/auth/switch-profile.dto";
import { UpgradeProfileDTO } from "utils/helpers/models/auth/upgrade-profile.dto";
import useFirebase from "hooks/useFirebse";
import { PictureDTO } from "utils/helpers/models/shared/picture.dto";
import { UserDTO } from "utils/helpers/models/shared/user.dto";
import { UserDetailDTO } from "utils/helpers/models/shared/user-detail.dto";

const useAuth = () => {
  const {
    setToken,
    setRegisteredData,
    setProfileModalOpen,
    isProfileCompleted,
    getDefaultPathByRole,
    getCurrentRole,
    setNotificationCount,
  } = useStore();
  const { getNotificationPermission } = useFirebase();

  const navigate = useNavigate();

  // Allowed roles for login
  const allowedRoles = [
    ROLES.ATTENDEE,
    ROLES.HOST,
    ROLES.CHILD, // minor
    ROLES.RSVP,
    ROLES.AMBASSADOR,
  ];

  // Helper function to validate if role is allowed
  const isRoleAllowed = (role: ROLES | null | undefined): boolean => {
    if (!role) return false;
    return allowedRoles.includes(role);
  };

  const businessRegister = async (body: CreateBusinessDTO) => {
    const response = await Auth_APIS.businessRegister(body);
    const { status = false, message = "" } = response || {};
    if (status) {
      navigate(siteRoutes.otpVerification, {
        state: { email: response.data.email, type: "signup" },
      });
    }
  };

  const veifyOtpForSignup = async (body: VerifyOtpDTO) => {
    const response = await Auth_APIS.verifyOtpSignup(body);
    const { status = false } = response || {};
    if (status) {
      const role = getCurrentRole();
      setAuthData(response);
      // Request notification permission after successful OTP verification
      // User is now verified and has a valid token
      getNotificationPermission();
      // Show modal immediately after signup if profile is not completed (for HOST role)
      // The home page will handle periodic reminders
      if (role === ROLES.HOST) {
        const profileCompleted = isProfileCompleted();
        if (!profileCompleted && window.location.pathname !== "/host-profile") {
          setProfileModalOpen(true);
        }
      }
    }
  };

    const verifyOtpForget = async (body: VerifyOtpDTO) => {
    const response = await Auth_APIS.verifyOtpForget(body);
    const { status = false, message = "" } = response || {};
    console.log(response, "response otp forget");
    if (status) {
      successToaster(message);
 
      

      // navigate(siteRoutes.otpVerification, {
      //   state: { email: response.data.email, type: "signup" },
      // });
      // if(response?.data?.email && response?.data?.otpCode) {
      //   navigate(siteRoutes.resetPassword, {
      //     state: { email: response?.data?.email, otp: response?.data?.otpCode },
      //   });
      // }
      return true;
    }
    return false;
  };

  const upgradeProfile = async (
    body: UpgradeProfileDTO,
    goBack: boolean = false
  ) => {
    const response = await Auth_APIS.upgradeProfie(body);
    const { status = false } = response || {};
    if (status) {
      setAuthData(response, false);
      if (goBack) navigate(-1);
    }
  };



  const login = async (body: LoginFormDTO) => {
    const response = await Auth_APIS.login(body);
    const { status = false } = response || {};
    if (status) {
      // Check role before proceeding with login
      const { user = null } = response.data || {};
      const userRole = user?.currentRole;
      
      if (!isRoleAllowed(userRole)) {
        errorToaster("Access denied. Only Attendee, Host, Minor, RSVP, or Ambassador roles are allowed to login.");
        return;
      }

      setAuthData(response);
      // Show modal immediately after login if profile is not completed (for HOST role)
      // The home page will handle periodic reminders
      if (userRole === ROLES.HOST) {
        const profileCompleted = isProfileCompleted();
        if (!profileCompleted && window.location.pathname !== "/host-profile") {
          setProfileModalOpen(true);
        }
      }
      // Only request notification permission if user is verified (has token)
      // This prevents 401 errors during OTP verification flow
      if (user?.verified) {
        getNotificationPermission();
      }
    }
  };

  const setAuthData = (response: any, shouldNavigate: boolean = true) => {
    console.log(response, "response");

    const { jwtToken = null, user = null } = response.data;
    if (user) {
      setRegisteredData(response?.data);
    }
    if (jwtToken) {
      const { user = null } = response?.data;
      console.log(user?.verified, "verified");
      console.log(shouldNavigate, "should navigate");
      if (user?.verified) {
        const { message } = response;
        delete response.data.jwtToken;
        setToken(jwtToken);
        successToaster(message);
        setRegisteredData(response?.data);
        if (shouldNavigate) {
          const path = getDefaultPathByRole();
          navigate(path, { replace: true });
        }
      } else {
        navigate(siteRoutes.otpVerification, {
          state: { email: user.email, type: "signup" },
        });
      }
    }
  };

  const resetPassword = async (body: ResetPasswordDTO) => {
    const response = await Auth_APIS.resetPassword(body);
    const { status = false, message = "" } = response || {};
    if (status) {
      successToaster(message);
      navigate(siteRoutes.login);
    }
  };

  const resendOTP = async (body: ResendOtpDTO, type: string = "reset") => {
    const response = await Auth_APIS.resendOtp(body);
    const { status = false, message = "" } = response || {};
    if (status) {
      successToaster(message);
      navigate(siteRoutes.otpVerification, {
        state: { email: body.email, type: type },
      });
    }
  };

  const loginByGoogle = async (body: LoginByGoogleDTO) => {
    const response = await Auth_APIS.loginByGoogle(body);
    const { status = false } = response || {};
    if (status) {
      // Check role before proceeding with login
      const { user = null } = response.data || {};
      const userRole = user?.currentRole;
      
      if (!isRoleAllowed(userRole)) {
        errorToaster("Access denied. Only Attendee, Host, Minor, RSVP, or Ambassador roles are allowed to login.");
        return;
      }

      setAuthData(response);
      // Only request notification permission if user is verified (has token)
      if (user?.verified) {
        getNotificationPermission();
      }
    }
  };

  const loginByApple = async (body: LoginByAppleDTO) => {
    const response = await Auth_APIS.loginByApple(body);
    const { status = false } = response || {};
    if (status) {
      // Check role before proceeding with login
      const { user = null } = response.data || {};
      const userRole = user?.currentRole;
      
      if (!isRoleAllowed(userRole)) {
        errorToaster("Access denied. Only Attendee, Host, Minor, RSVP, or Ambassador roles are allowed to login.");
        return;
      }

      setAuthData(response);
      // Only request notification permission if user is verified (has token)
      if (user?.verified) {
        getNotificationPermission();
      }
    }
  };

  const getUserProfile = async () => {
    const response = await Auth_APIS.getUserProfile();
    const { data = null, status = false } = response || {};
    if (status) {
      setRegisteredData(data);
    }
  };

  const getUserById = async (userId: number, setData: Function) => {
    const response = await Auth_APIS.getUserById(userId);
    const { data = {}, status = false } = response || {};
    if (status) {
      setData(new UserDetailDTO(data));
    }
  };

  const uploadFile = async (file: File): Promise<PictureDTO | null> => {
    const body = new FormData();
    body.append("file", file);
    const response = await Auth_APIS.uploadFile(body);
    const { status = false, data } = response || {};
    if (status) {
      return new PictureDTO(data);
    } else {
      return null;
    }
  };

  const getStates = async (setData: Function) => {
    const response = await Auth_APIS.getStates();
    const data = await response.json();
    setData(data.states);
  };

  const switchProfile = async (body: SwitchProfileDTO) => {
    const response = await Auth_APIS.switchProfile(body);
    const { status = false } = response || {};
    if (status) {
      setAuthData(response);
    }
  };

  const setDefaultRole = async (body: SwitchProfileDTO) => {
    const response = await Auth_APIS.setDefaultRole(body);
    const { status = false } = response || {};
    if (status) {
      setAuthData(response, false);
    }
  };

  const getNotifications = async (setData: Function, params?: { offset?: number; limit?: number }) => {
    const response = await Auth_APIS.getNotification(params);
    const { data = null, status = false } = response || {};
    if (status) {
      setData(data.notifications);
    }
    return { data, status };
  };

  const getNotificationCount = async () => {
    const response = await Auth_APIS.getNotificationCount();
    const { data = null, status = false } = response || {};
    setNotificationCount(data?.unreadNotifications || 0);
  };

  const markAllNotificationAsRead = async () => {
    const response = await Auth_APIS.markAllNotificationAsRead();
    const { status = false } = response || {};
    if (status) {
      getNotificationCount();
    }
  };

  // const markNotificationAsRead = async (id: number) => {
  //   const response = await Auth_APIS.markNotificationAsRead(id);
  //   const { status = false } = response || {};
  //   if (status) {
  //     getNotificationCount();
  //   }
  // }

  
  const markNotificationAsRead = async (id: number) => {
    const response = await Auth_APIS.markNotificationAsRead(id);
    const { status = false } = response || {};
    if (status) {
      getNotificationCount();
    }
    return status; // ✅ return status here
  };
  // const getPrivacyPolicy = async (body: any,setPrivacyPolicyContent?:Function) => {
  //   const response = await Auth_APIS.getPrivacyPolicy(body);
  //   const { status = false , data = {}} = response || {};
  //   if (status) {
  //     const { content = [] } = data;
  //     setPrivacyPolicyContent(content);
  //     // getNotificationCount();
  //   }
  //   return status; // ✅ return status here
  // };

  const getPrivacyPolicy = async (
    body: any,
    setPrivacyPolicyContent?: Function
  ): Promise<any> => {
    const response = await Auth_APIS.getPrivacyPolicy(body);
    const { status = false, data = {} } = response || {};

    if (status) {
      const { content = [] } = data;
      if (setPrivacyPolicyContent) {
        setPrivacyPolicyContent(content);
      }
      return content;
    }

    return [];
  };

  return {
    businessRegister,
    veifyOtpForSignup,
    login,
    loginByGoogle,
    loginByApple,
    verifyOtpForget,
    resetPassword,
    resendOTP,
    getUserProfile,
    uploadFile,
    getStates,
    switchProfile,
    setDefaultRole,
    upgradeProfile,
    setAuthData,
    getUserById,
    getNotifications,
    getNotificationCount,
    markNotificationAsRead,
    getPrivacyPolicy,
    markAllNotificationAsRead,
  };
};

export default useAuth;
